import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Gem, Sparkles, Handshake, Store, MapPin, Wallet, ChevronDown, CalendarClock } from "lucide-react";
import { BOOKING_INFO, HENNA_PRICING, VENDOR_INFO, TRAVEL_INFO, AVAILABILITY } from "../../lib/site-data";
import { SparkleBurst } from "./Sparkle";

const ICON_MAP = { Clock, Gem, Sparkles, Handshake, Wallet };

export const BookingInfo = () => {
    const [showHenna, setShowHenna] = useState(false);
    const [showAvail, setShowAvail] = useState(false);

    return (
        <section
            id="pricing"
            data-testid="booking-info-section"
            className="py-16 sm:py-20 holo-radial grain relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
                <div className="text-center mb-10">
                    <p className="text-xs tracking-[0.25em] uppercase text-brand-coral mb-3">Booking & info</p>
                    <h2 className="font-display text-3xl sm:text-5xl text-brand-ink leading-[1.05] max-w-3xl mx-auto">
                        Simple, friendly <em className="text-brand-coral">booking</em>.
                    </h2>
                    <p className="mt-3 text-sm text-brand-inkSoft max-w-xl mx-auto">
                        Custom quote based on your guest count, hours, and services. I'll send pricing
                        with my reply within 48 hours.
                    </p>
                </div>

                {/* Booking info cards — compact */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                    {BOOKING_INFO.map((b, i) => {
                        const Icon = ICON_MAP[b.icon] || Sparkles;
                        return (
                            <motion.div
                                key={b.title}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.05 }}
                                className="bg-white rounded-xl border border-brand-border p-4 flex flex-col gap-2"
                                data-testid={`booking-info-${i}`}
                            >
                                <span className="h-9 w-9 rounded-lg bg-brand-coral/10 flex items-center justify-center">
                                    <Icon className="h-4 w-4 text-brand-coral" strokeWidth={1.7} />
                                </span>
                                <h3 className="font-display text-base text-brand-ink leading-tight">{b.title}</h3>
                                <p className="text-xs text-brand-inkSoft leading-snug">{b.desc}</p>
                                {b.methods && (
                                    <div className="flex flex-wrap gap-1 mt-0.5" data-testid="payment-methods">
                                        {b.methods.map((m) => (
                                            <span
                                                key={m.label}
                                                style={{ background: m.bg, color: m.fg }}
                                                className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded shadow-sm"
                                                data-testid={`payment-method-${m.label.toLowerCase()}`}
                                            >
                                                {m.label}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

                {/* Availability collapsible */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-xl border border-brand-border mb-6 overflow-hidden"
                    data-testid="availability-card"
                >
                    <button
                        type="button"
                        onClick={() => setShowAvail(!showAvail)}
                        data-testid="availability-toggle"
                        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left group"
                    >
                        <div className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-lg bg-brand-coral/10 flex items-center justify-center group-hover:bg-brand-coral/20 transition-colors">
                                <CalendarClock className="h-4 w-4 text-brand-coral" strokeWidth={1.7} />
                            </span>
                            <div>
                                <p className="text-[10px] tracking-[0.2em] uppercase text-brand-coral">Booking availability</p>
                                <h3 className="font-display text-base text-brand-ink leading-tight">When I'm available to play</h3>
                            </div>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-brand-inkSoft transition-transform ${showAvail ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                        {showAvail && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                style={{ overflow: "hidden" }}
                            >
                                <div className="px-5 pb-5 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 border-t border-brand-border">
                                    {AVAILABILITY.map((a) => (
                                        <div key={a.day} className="flex items-baseline justify-between gap-3 py-1.5 border-b border-brand-border/50 last:border-0">
                                            <span className="text-sm font-medium text-brand-ink">{a.day}</span>
                                            <span className="text-xs text-brand-inkSoft text-right">{a.hours}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    {/* Vendor card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-2 bg-brand-ink text-brand-cream rounded-2xl p-6 relative overflow-hidden"
                        data-testid="vendor-card"
                    >
                        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-brand-coral/30 blur-3xl" />
                        <div className="relative">
                            <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-brand-gold mb-4">
                                <Store className="h-4 w-4" /> Vendor & Community Partnerships
                            </span>
                            <h3 className="font-display text-2xl sm:text-3xl leading-tight mb-2">
                                {VENDOR_INFO.desc}
                            </h3>
                            <p className="text-white/70 text-xs leading-relaxed mb-4">
                                {VENDOR_INFO.longDesc}
                            </p>
                            <ul className="space-y-1.5 text-white/80 text-sm">
                                {VENDOR_INFO.items.map((it) => (
                                    <li key={it} className="flex items-start gap-2">
                                        <span className="text-brand-gold mt-1 text-[10px]">✦</span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                            <SparkleBurst count={14} className="mt-5">
                                <a
                                    href="#inquiry"
                                    data-testid="vendor-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-cream text-brand-ink px-5 py-2.5 text-xs hover:bg-brand-coralLight hover:text-white transition-colors"
                                >
                                    Inquire as a vendor →
                                </a>
                            </SparkleBurst>
                        </div>
                    </motion.div>

                    {/* Henna per-design table — collapsible */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="lg:col-span-3 bg-white rounded-2xl border border-brand-border p-5 sm:p-6"
                        data-testid="henna-pricing-card"
                    >
                        <button
                            type="button"
                            onClick={() => setShowHenna(!showHenna)}
                            data-testid="henna-pricing-toggle"
                            className="w-full flex items-center justify-between text-left group"
                        >
                            <div>
                                <p className="text-xs tracking-[0.25em] uppercase text-brand-coral mb-1.5">Private Henna Sessions</p>
                                <h3 className="font-display text-xl sm:text-2xl text-brand-ink leading-tight">
                                    Per-design pricing
                                </h3>
                                <p className="text-xs text-brand-inkSoft mt-1.5">{HENNA_PRICING.intro}</p>
                            </div>
                            <span className={`shrink-0 ml-4 h-9 w-9 rounded-full bg-brand-coral/10 flex items-center justify-center group-hover:bg-brand-coral/20 transition-all ${showHenna ? "rotate-180" : ""} duration-500`}>
                                <ChevronDown className="h-4 w-4 text-brand-coral" />
                            </span>
                        </button>

                        <AnimatePresence initial={false}>
                            {showHenna && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto", marginTop: 24 }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                    style={{ overflow: "hidden" }}
                                >
                                    <div className="space-y-7 pt-2 border-t border-brand-border">
                                        {HENNA_PRICING.tiers.map((tier) => (
                                            <div key={tier.name} className="pt-5">
                                                <div className="flex items-baseline justify-between border-b border-brand-border pb-2 mb-3">
                                                    <h4 className="font-display text-xl text-brand-ink">{tier.name}</h4>
                                                </div>
                                                <ul className="space-y-2">
                                                    {tier.rows.map((r) => (
                                                        <li key={r.label} className="flex items-baseline justify-between gap-4 text-[15px]">
                                                            <span className="text-brand-ink">{r.label}</span>
                                                            <span className="text-brand-coral font-display text-lg tabular-nums">{r.price}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>

                <div className="mt-6 flex items-center justify-center gap-2 text-xs text-brand-inkSoft text-center max-w-2xl mx-auto">
                    <MapPin className="h-3.5 w-3.5 text-brand-coral shrink-0" />
                    <span>{TRAVEL_INFO}</span>
                </div>
            </div>
        </section>
    );
};

export default BookingInfo;
